import React, { useEffect, useRef, useState, useMemo } from 'react';
import Map from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import OSM from 'ol/source/OSM';
import TileWMS from 'ol/source/TileWMS';
import VectorSource from 'ol/source/Vector';
import Draw from 'ol/interaction/Draw';
import { Type as GeometryType } from 'ol/geom/Geometry';
import Point from 'ol/geom/Point';
import { getArea, getLength } from 'ol/sphere';
import { Style, Fill, Stroke, Circle as CircleStyle, Icon } from 'ol/style';
import WKT from 'ol/format/WKT';
import 'ol/ol.css';
import { 
  Slider, Typography, Paper, Button, Stack, Divider, TextField, 
  Dialog, DialogTitle, DialogContent, DialogActions, 
  MenuItem, Select, InputLabel, FormControl, IconButton, List, ListItem, ListItemText, Link,
  ThemeProvider, CssBaseline, InputAdornment, AppBar, Toolbar, Drawer, Box, ListItemButton, ListItemIcon, Collapse, Alert,
  ToggleButton, ToggleButtonGroup
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import MapIcon from '@mui/icons-material/Map';
import MenuIcon from '@mui/icons-material/Menu';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import LayersIcon from '@mui/icons-material/Layers';
import EditLocationIcon from '@mui/icons-material/EditLocation';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import PersonIcon from '@mui/icons-material/Person';
import LockIcon from '@mui/icons-material/Lock';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import { SnackbarProvider, useSnackbar } from 'notistack';
import { darkTheme, lightTheme } from './theme';

type Role = 'guest' | 'operator' | 'admin';

const roleNames = {
  guest: 'Гость',
  operator: 'Оператор',
  admin: 'Администратор'
};

const getRussianType = (type: string) => {
  if (type === 'Point') return 'Точка';
  if (type === 'LineString') return 'Линия';
  if (type === 'Polygon') return 'Полигон';
  return type;
};

function MapApp({ toggleTheme, mode }: { toggleTheme: () => void, mode: 'light' | 'dark' }) {
  const { enqueueSnackbar } = useSnackbar();
  const mapElement = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<Map | null>(null);
  const [opacity, setOpacity] = useState<number>(1);
  const [drawType, setDrawType] = useState<GeometryType | null>(null);
  const [savedDrawType, setSavedDrawType] = useState<string>('');
  const [measureResult, setMeasureResult] = useState<string>('');
  
  const [currentMeasureUnit, setCurrentMeasureUnit] = useState<'metric' | 'miles'>('metric');
  const [currentAreaUnit, setCurrentAreaUnit] = useState<'km2' | 'm2' | 'ha'>('km2');

  const [isControlPanelOpen, setIsControlPanelOpen] = useState(true);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const [userRole, setUserRole] = useState<Role>('guest');
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [targetRole, setTargetRole] = useState<Role | null>(null);
  const [loginUsername, setLoginUsername] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');

  const canEdit = userRole === 'operator' || userRole === 'admin';
  const canDelete = userRole === 'admin';

  const handleRoleChangeAttempt = (newRole: Role) => {
    if (newRole === 'guest') {
      setUserRole('guest');
      enqueueSnackbar('Вы вошли как Гость', { variant: 'info' });
    } else {
      setTargetRole(newRole);
      setIsLoginModalOpen(true);
      setLoginError('');
      setLoginUsername('');
      setLoginPassword('');
    }
  };

  const handleLoginSubmit = () => {
    if (targetRole === 'operator' && loginUsername === 'operator' && loginPassword === '123') {
      setUserRole('operator');
      setIsLoginModalOpen(false);
      enqueueSnackbar('Вы вошли как Оператор', { variant: 'success' });
    } else if (targetRole === 'admin' && loginUsername === 'admin' && loginPassword === '1q2w3e4rGEO') {
      setUserRole('admin');
      setIsLoginModalOpen(false);
      enqueueSnackbar('Вы вошли как Администратор', { variant: 'success' });
    } else {
      setLoginError('Неверный логин или пароль');
    }
  };

  const drawTypeRef = useRef<GeometryType | null>(null);

  const [objectsList, setObjectsList] = useState<any[]>([]);
  const [objSearchQuery, setObjSearchQuery] = useState('');

  const [orthoList, setOrthoList] = useState<any[]>([
    { id: 1, name: 'Новосельцево (Тестовый)', date: '2026-11-04', layer: 'primorye:Novo', x: 14772168.94, y: 5585872.74, visible: true, desc: 'Тестовый ортофотоплан', files: [] }
  ]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isOrthoInfoModalOpen, setIsOrthoInfoModalOpen] = useState(false);
  const [currentOrthoInfo, setCurrentOrthoInfo] = useState<any>(null);

  const [openGroups, setOpenGroups] = useState<{ [key: string]: boolean }>({
    'Ортофотопланы': true,
    'Объекты': true,
    'Точки': true,
    'Линии': true,
    'Полигоны': true
  });

  const toggleGroup = (group: string) => {
    setOpenGroups(prev => ({ ...prev, [group]: !prev[group] }));
  };

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [objectToDelete, setObjectToDelete] = useState<number | null>(null);

  const [editingObjectId, setEditingObjectId] = useState<number | null>(null); 
  const [currentFeature, setCurrentFeature] = useState<any>(null);
  const [infoData, setInfoData] = useState<any>(null);

  const [objectName, setObjectName] = useState('');
  const [objectCategory, setObjectCategory] = useState('Инфраструктура');
  const [objectDesc, setObjectDesc] = useState('');
  const [objectIcon, setObjectIcon] = useState('default');
  const [objectHeight, setObjectHeight] = useState<number>(0);
  const [customFields, setCustomFields] = useState([{ key: '', value: '' }]);
  const [uploadedFiles, setUploadedFiles] = useState<any[]>([]);

  const vectorSource = useRef(new VectorSource());

  const handleZoomToOrtho = (x: number, y: number) => {
    if (map) {
      map.getView().animate({ center: [x, y], zoom: 15, duration: 1000 });
      setIsDrawerOpen(false);
    }
  };

  const zoomToFeature = (featureId: number) => {
    if (!map) return;
    const feature = vectorSource.current.getFeatureById(featureId);
    if (!feature) return;

    const geometry = feature.getGeometry();
    if (!geometry) return;

    let center;
    if (geometry instanceof Point) {
      center = geometry.getCoordinates();
    } else {
      const extent = geometry.getExtent();
      center = [(extent[0] + extent[2]) / 2, (extent[1] + extent[3]) / 2];
    }

    map.getView().animate({ center: center, zoom: 17, duration: 1000 });

    const originalStyle = feature.getStyle();
    feature.setStyle(new Style({
      fill: new Fill({ color: 'rgba(34, 197, 94, 0.5)' }),
      stroke: new Stroke({ color: '#22c55e', width: 5 }),
      image: new CircleStyle({ radius: 10, fill: new Fill({ color: '#22c55e' }), stroke: new Stroke({ color: '#fff', width: 3 }) }),
    }));

    setTimeout(() => {
      feature.setStyle(originalStyle);
    }, 2000);

    setIsInfoModalOpen(false);
    setIsDrawerOpen(false);
  };

  const toggleOrthoVisibility = (id: number) => {
    const newList = orthoList.map(o => o.id === id ? { ...o, visible: !o.visible } : o);
    setOrthoList(newList);
    if (map) {
        const layer = (map as any).orthoLayer;
        if (layer) {
            const anyVisible = newList.some(o => o.visible);
            layer.setOpacity(anyVisible ? opacity : 0);
        }
    }
  };

  const loadObjectsFromDB = () => {
    fetch('http://10.5.10.101:8000/api/objects')
      .then(res => res.json())
      .then(data => {
        const objectsWithVisibility = data.map((o: any) => ({ ...o, visible: true }));
        setObjectsList(objectsWithVisibility);
        
        const format = new WKT();
        vectorSource.current.clear();
        
        objectsWithVisibility.forEach((obj: any) => {
          const feature = format.readFeature(obj.wkt, {
            dataProjection: 'EPSG:3857',
            featureProjection: 'EPSG:3857'
          });
          if (feature) {
            feature.setId(obj.id);
            feature.set('name', obj.name);
            feature.set('description', obj.description);
            feature.set('attributes', obj.attributes);
            feature.set('type', obj.type);
            feature.set('file_urls', obj.file_urls || []);
            feature.set('icon_type', obj.icon_type);
            feature.set('height', obj.height || 0); 
            
            if (obj.type === 'Point' && obj.icon_type && obj.icon_type !== 'default') {
              feature.setStyle(new Style({
                image: new Icon({
                  src: `https://cdn.jsdelivr.net/gh/pointhi/leaflet-color-markers@master/img/marker-icon-2x-${obj.icon_type}.png`,
                  scale: 1,
                  anchor: [0.5, 1]
                })
              }));
            }
            vectorSource.current.addFeature(feature);
          }
        });
      })
      .catch(err => console.error("Ошибка загрузки:", err));
  };

  const toggleFeatureVisibility = (id: number) => {
    const feature = vectorSource.current.getFeatureById(id);
    if (feature) {
      const isNowVisible = feature.get('visible') !== false;
      feature.set('visible', !isNowVisible);
      feature.setStyle(isNowVisible ? new Style({}) : undefined);
      setObjectsList(prev => prev.map(o => o.id === id ? { ...o, visible: !isNowVisible } : o));
    }
  };

  const toggleGroupVisibility = (group: string, isVisible: boolean) => {
    const updatedObjects = objectsList.map(obj => {
      const objGroup = obj.type === 'Point' ? 'Точки' : obj.type === 'LineString' ? 'Линии' : 'Полигоны';
      if (objGroup === group) {
        const feature = vectorSource.current.getFeatureById(obj.id);
        if (feature) {
          feature.set('visible', isVisible);
          feature.setStyle(isVisible ? undefined : new Style({}));
        }
        return { ...obj, visible: isVisible };
      }
      return obj;
    });
    setObjectsList(updatedObjects);
  };

  const confirmDeleteObject = (id: number) => {
    setObjectToDelete(id);
    setDeleteModalOpen(true);
  };

  const executeDelete = async () => {
    if (objectToDelete === null) return;
    try {
      await fetch(`http://10.5.10.101:8000/api/objects/${objectToDelete}`, { method: 'DELETE' });
      
      const feature = vectorSource.current.getFeatureById(objectToDelete);
      if (feature) vectorSource.current.removeFeature(feature);
      setObjectsList(objectsList.filter(o => o.id !== objectToDelete));
      enqueueSnackbar("Объект удален из базы данных", { variant: 'success' });
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Ошибка при удалении из базы", { variant: 'error' });
    } finally {
      setDeleteModalOpen(false);
      setObjectToDelete(null);
      setIsInfoModalOpen(false);
    }
  };

  const openObjectInfo = (obj: any) => {
    setInfoData(obj); 
    setIsInfoModalOpen(true);
  };

  const openEditModal = () => {
    if (!infoData) return;
    
    setEditingObjectId(infoData.id);
    setSavedDrawType(infoData.type);
    setObjectName(infoData.name || '');
    setObjectCategory('Инфраструктура');
    setObjectDesc(infoData.description || '');
    setObjectIcon(infoData.icon_type || 'default');
    setObjectHeight(infoData.height || 0);
    setUploadedFiles(infoData.file_urls || []);
    
    const attrs = infoData.attributes || {};
    const fields = Object.keys(attrs).map(key => ({ key, value: attrs[key] }));
    setCustomFields(fields.length > 0 ? fields : [{ key: '', value: '' }]);

    setIsInfoModalOpen(false);
    setIsCreateModalOpen(true);
  };

  const openOrthoInfo = (ortho: any) => {
    setCurrentOrthoInfo(ortho);
    setIsOrthoInfoModalOpen(true);
  };

  useEffect(() => {
    if (!mapElement.current) return;

    const baseLayer = new TileLayer({ source: new OSM() });
    const orthoLayer = new TileLayer({
      source: new TileWMS({
        url: 'http://10.5.10.101:8080/geoserver/primorye/wms',
        params: { 'LAYERS': 'primorye:Novo', 'TILED': true },
        serverType: 'geoserver',
      }),
      opacity: 1,
    });

    const vectorLayer = new VectorLayer({
      source: vectorSource.current,
      style: new Style({
        fill: new Fill({ color: 'rgba(164, 49, 80, 0.2)' }),
        stroke: new Stroke({ color: '#a43150', width: 3 }),
        image: new CircleStyle({ radius: 7, fill: new Fill({ color: '#a43150' }), stroke: new Stroke({ color: '#f4f6fa', width: 2 }) }),
      }),
    });

    const initialMap = new Map({
      target: mapElement.current,
      layers: [baseLayer, orthoLayer, vectorLayer],
      view: new View({ center: [14772168.94, 5585872.74], zoom: 13 }),
    });

    setMap(initialMap);
    (initialMap as any).orthoLayer = orthoLayer;

    initialMap.on('singleclick', (evt) => {
      if (drawTypeRef.current) return;
      const feature = initialMap.forEachFeatureAtPixel(evt.pixel, (f) => f);
      
      if (feature && feature.getId()) {
        evt.preventDefault();
        evt.stopPropagation();
        
        let coords = ["0", "0"];
        const geom = feature.getGeometry();
        
        if (geom instanceof Point) {
          const c = geom.getCoordinates();
          coords = [c[0].toFixed(2), c[1].toFixed(2)];
        } else if (geom) {
          const extent = geom.getExtent();
          coords = [((extent[0] + extent[2]) / 2).toFixed(2), ((extent[1] + extent[3]) / 2).toFixed(2)];
        }

        setInfoData({
          id: feature.getId(),
          name: feature.get('name'),
          description: feature.get('description'),
          type: feature.get('type'),
          attributes: feature.get('attributes'),
          file_urls: feature.get('file_urls') || [],
          icon_type: feature.get('icon_type'),
          height: feature.get('height'),
          coords: coords,
          rawGeometry: geom 
        });
        setIsInfoModalOpen(true);
      }
    });

    loadObjectsFromDB();
    return () => initialMap.setTarget(undefined);
  }, []);

  useEffect(() => {
    drawTypeRef.current = drawType;
    if (!map || !drawType) return;

    const draw = new Draw({ source: vectorSource.current, type: drawType });
    map.addInteraction(draw);

    draw.on('drawend', (event) => {
      const feature = event.feature;
      const geometry = feature.getGeometry();
      if (!geometry) return;

      setEditingObjectId(null); 
      setSavedDrawType(drawType);
      setCurrentFeature(feature);
      setObjectName('');
      setObjectDesc('');
      setObjectCategory('Инфраструктура');
      setObjectIcon('default');
      setObjectHeight(0);
      setCustomFields([{ key: '', value: '' }]);
      setUploadedFiles([]);
      setIsCreateModalOpen(true);
      setDrawType(null); 
    });

    return () => { map.removeInteraction(draw); };
  }, [map, drawType]);

  const handleAddField = () => setCustomFields([...customFields, { key: '', value: '' }]);
  const handleRemoveField = (index: number) => setCustomFields(customFields.filter((_, i) => i !== index));
  const handleFieldChange = (index: number, field: 'key' | 'value', val: string) => {
    const newFields = [...customFields];
    newFields[index][field] = val;
    setCustomFields(newFields);
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const file = e.target.files[0];
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch('http://10.5.10.101:8000/api/upload', { method: 'POST', body: formData });
      const data = await response.json();
      setUploadedFiles(prev => [...prev, { name: file.name, url: data.url }]);
      enqueueSnackbar(`Файл ${file.name} прикреплен!`, { variant: 'success' });
    } catch (err) {
      console.error("Ошибка загрузки файла", err);
      enqueueSnackbar("Ошибка загрузки файла", { variant: 'error' });
    }
  };

  const handleSaveObject = async () => {
    if (!currentFeature && !editingObjectId) return;

    let wktString = "";
    if (currentFeature) {
      const geometry = currentFeature.getGeometry();
      const format = new WKT();
      wktString = format.writeGeometry(geometry);
    }

    const attrObject = customFields.reduce((acc, curr) => {
      if (curr.key && curr.value) acc[curr.key] = curr.value;
      return acc;
    }, {} as Record<string, string>);

    const payload = {
      name: objectName || 'Без названия',
      type: savedDrawType,
      description: objectDesc,
      attributes: attrObject,
      icon_type: objectIcon,
      file_urls: uploadedFiles,
      height: objectHeight,
      wkt: wktString
    };

    try {
      let res;
      if (editingObjectId) {
        res = await fetch(`http://10.5.10.101:8000/api/objects/${editingObjectId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...payload, wkt: infoData.wkt || wktString })
        });
      } else {
        res = await fetch('http://10.5.10.101:8000/api/objects', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      }

      if (!res.ok) {
        const errorData = await res.json();
        enqueueSnackbar("Ошибка сервера: " + JSON.stringify(errorData), { variant: 'error' });
        throw new Error("Ошибка сохранения");
      }

      enqueueSnackbar(editingObjectId ? "Объект обновлен" : "Объект создан", { variant: 'success' });
      setIsCreateModalOpen(false);
      setCurrentFeature(null);
      setEditingObjectId(null);
      loadObjectsFromDB();
      
    } catch (err) {
      console.error("Ошибка сохранения:", err);
    }
  };

  const handleCancelObject = () => {
    if (currentFeature) {
      vectorSource.current.removeFeature(currentFeature);
      if (map) map.render();
    }
    setIsCreateModalOpen(false);
    setCurrentFeature(null);
    setEditingObjectId(null);
  };

  const handleOpacityChange = (event: Event, newValue: number | number[]) => {
    const val = newValue as number;
    setOpacity(val);
    if (map) {
      const layer = (map as any).orthoLayer;
      if (layer) layer.setOpacity(val);
    }
  };

  const clearDrawings = () => {
    vectorSource.current.clear();
    setMeasureResult('');
    if (map) map.render();
  };

  const calculateMetrics = (geometry: any) => {
    if (!geometry) return null;
    
    let lengthM = 0;
    let areaM2 = 0;

    if (geometry.getType() === 'LineString' || geometry.getType() === 'Polygon') {
      lengthM = getLength(geometry);
    }
    if (geometry.getType() === 'Polygon') {
      areaM2 = getArea(geometry);
    }

    return { lengthM, areaM2 };
  };

  const formatLength = (meters: number, unit: 'metric' | 'miles') => {
    if (unit === 'miles') {
      return `${(meters * 0.000621371).toFixed(2)} миль`;
    }
    if (meters < 1000) return `${meters.toFixed(1)} м`;
    return `${(meters / 1000).toFixed(2)} км`;
  };

  const formatArea = (sqMeters: number, unit: 'km2' | 'm2' | 'ha') => {
    if (unit === 'ha') return `${(sqMeters / 10000).toFixed(2)} га`;
    if (unit === 'm2') return `${sqMeters.toFixed(1)} м²`;
    return `${(sqMeters / 1000000).toFixed(2)} км²`;
  };

  const filteredOrthos = orthoList.filter(o => o.name.toLowerCase().includes(searchQuery.toLowerCase()));
  
  const groupedObjects = useMemo(() => {
    const filtered = objectsList.filter(o => o.name.toLowerCase().includes(objSearchQuery.toLowerCase()));
    return filtered.reduce((acc: any, obj: any) => {
      const group = obj.type === 'Point' ? 'Точки' : obj.type === 'LineString' ? 'Линии' : 'Полигоны';
      if (!acc[group]) acc[group] = [];
      acc[group].push(obj);
      return acc;
    }, {});
  }, [objectsList, objSearchQuery]);

  const renderGroupSection = (groupName: string, items: any[], onItemClick: (item: any) => void, onToggleVisibility: (id: number) => void) => {
    const allVisible = items.every(o => o.visible !== false);
    
    return (
      <div style={{ marginBottom: 8 }} key={groupName}>
        <div style={{ display: 'flex', alignItems: 'center', backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)', borderRadius: 6, paddingRight: 8 }}>
          <ListItemButton onClick={() => toggleGroup(groupName)} style={{ paddingTop: 8, paddingBottom: 8, flexGrow: 1 }}>
            <ListItemText primary={<Typography variant="subtitle2" style={{ fontWeight: 'bold', letterSpacing: '0.5px' }}>{groupName.toUpperCase()}</Typography>} />
            {openGroups[groupName] ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
          </ListItemButton>
          
          <IconButton size="small" onClick={() => {
             items.forEach(item => onToggleVisibility(item.id));
          }} title="Скрыть/показать всю группу">
            {allVisible ? <VisibilityOffIcon fontSize="small" style={{ color: '#e05b78' }} /> : <VisibilityIcon fontSize="small" style={{ color: '#22c55e' }} />}
          </IconButton>
        </div>
        
        <Collapse in={openGroups[groupName]} timeout="auto" unmountOnExit>
          <List disablePadding style={{ paddingLeft: 8, paddingTop: 4 }}>
            {items.map((obj: any) => (
              <ListItem key={obj.id} disablePadding style={{ marginBottom: 2 }}>
                <ListItemButton onClick={() => onItemClick(obj)} style={{ borderRadius: 6 }}>
                  <ListItemIcon style={{ minWidth: 30 }}><EditLocationIcon fontSize="small" style={{ color: '#a9b4c7' }} /></ListItemIcon>
                  <ListItemText primary={<Typography style={{ fontSize: '14px' }}>{obj.name}</Typography>} />
                  
                  <IconButton edge="end" size="small" onClick={(e) => { e.stopPropagation(); onToggleVisibility(obj.id); }}>
                    {obj.visible === false ? <VisibilityOffIcon fontSize="small" style={{ color: '#a9b4c7' }} /> : <VisibilityIcon fontSize="small" style={{ color: '#22c55e' }} />}
                  </IconButton>

                  {canDelete && (
                    <IconButton edge="end" size="small" onClick={(e) => { e.stopPropagation(); confirmDeleteObject(obj.id); }} style={{ color: '#e05b78', marginLeft: 5 }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Collapse>
      </div>
    );
  };

  const renderMetrics = () => {
    if (!infoData || !infoData.rawGeometry) return null;
    const metrics = calculateMetrics(infoData.rawGeometry);
    if (!metrics) return null;

    return (
      <div style={{ marginTop: 16, padding: 12, backgroundColor: 'rgba(48, 68, 99, 0.4)', borderRadius: 6, border: '1px solid #304463' }}>
        <Typography variant="subtitle2" color="textSecondary" gutterBottom><strong>ИЗМЕРЕНИЯ:</strong></Typography>
        
        {metrics.lengthM > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <Typography variant="body2">Длина:</Typography>
            <ToggleButtonGroup
              size="small"
              value={currentMeasureUnit}
              exclusive
              onChange={(e, val) => val && setCurrentMeasureUnit(val)}
            >
              <ToggleButton value="metric">{formatLength(metrics.lengthM, 'metric')}</ToggleButton>
              <ToggleButton value="miles">{formatLength(metrics.lengthM, 'miles')}</ToggleButton>
            </ToggleButtonGroup>
          </div>
        )}

        {metrics.areaM2 > 0 && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <Typography variant="body2">Площадь:</Typography>
              <ToggleButtonGroup
                size="small"
                value={currentAreaUnit}
                exclusive
                onChange={(e, val) => val && setCurrentAreaUnit(val)}
              >
                <ToggleButton value="m2">{formatArea(metrics.areaM2, 'm2')}</ToggleButton>
                <ToggleButton value="ha">{formatArea(metrics.areaM2, 'ha')}</ToggleButton>
                <ToggleButton value="km2">{formatArea(metrics.areaM2, 'km2')}</ToggleButton>
              </ToggleButtonGroup>
            </div>
            
            {infoData.height > 0 && (
              <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                  <Typography variant="body2"><strong>Высота:</strong></Typography>
                  <Typography variant="body2">{infoData.height} м</Typography>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2"><strong>Объем:</strong></Typography>
                  <Typography variant="body2" style={{ color: '#c15b75', fontWeight: 'bold' }}>
                    {(metrics.areaM2 * infoData.height).toFixed(2)} м³
                  </Typography>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <div style={{ width: '100vw', height: '100vh', position: 'relative', display: 'flex', flexDirection: 'column' }}>
      
      <AppBar position="static" color="transparent" elevation={0} style={{ background: mode === 'dark' ? 'rgba(10, 23, 43, 0.95)' : 'rgba(255, 255, 255, 0.95)', borderBottom: '1px solid rgba(164, 49, 80, 0.3)', zIndex: 1100 }}>
        <Toolbar style={{ justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <IconButton edge="start" color="inherit" onClick={() => setIsDrawerOpen(true)} style={{ marginRight: 16 }}>
              <MenuIcon />
            </IconButton>
            
            <div style={{ 
              width: 35, height: 35, borderRadius: '50%', backgroundColor: mode === 'dark' ? '#102544' : '#fff', 
              display: 'flex', justifyContent: 'center', alignItems: 'center', 
              boxShadow: '0 0 10px rgba(224, 91, 120, 0.4)', marginRight: 15, overflow: 'hidden',
              border: '2px solid #a43150', padding: 2
            }}>
              <img src="/logo.png" alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            </div>
            
            <div>
              <Typography variant="h6" style={{ fontWeight: 'bold', lineHeight: 1.1, fontSize: '16px' }}>
                {roleNames[userRole]}
              </Typography>
              <Typography variant="caption" color="textSecondary">ЕНЦ ПК 112</Typography>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
             <FormControl size="small" variant="outlined" style={{ minWidth: 180 }}>
                <Select 
                  value={userRole} 
                  onChange={(e) => handleRoleChangeAttempt(e.target.value as Role)}
                  style={{ fontSize: '12px', height: 36 }}
                  startAdornment={<InputAdornment position="start"><PersonIcon fontSize="small" /></InputAdornment>}
                >
                  <MenuItem value="guest">Войти как Гость</MenuItem>
                  <MenuItem value="operator">Войти как Оператор</MenuItem>
                  <MenuItem value="admin">Войти как Администратор</MenuItem>
                </Select>
             </FormControl>

            <IconButton color="inherit" onClick={toggleTheme}>
              {mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
            </IconButton>
            <div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#22c55e', margin: '0 10px' }} title="Сервер доступен"></div>
          </div>
        </Toolbar>
      </AppBar>

      <div style={{ flex: 1, position: 'relative' }}>
        <div ref={mapElement} style={{ width: '100%', height: '100%' }} />
        
        {!isControlPanelOpen && (
          <IconButton 
            onClick={() => setIsControlPanelOpen(true)}
            style={{ position: 'absolute', top: 20, right: 20, zIndex: 1000, backgroundColor: mode === 'dark' ? '#102544' : '#fff', boxShadow: '0 0 10px rgba(0,0,0,0.5)', border: '1px solid #a43150' }}
          >
            <ChevronLeftIcon style={{ color: '#a43150' }} />
          </IconButton>
        )}

        {isControlPanelOpen && (
          <Paper elevation={3} style={{ position: 'absolute', top: 20, right: 20, padding: 20, width: 320, zIndex: 1000 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 15 }}>
              <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>УПРАВЛЕНИЕ СЛОЕМ</Typography>
              <IconButton size="small" onClick={() => setIsControlPanelOpen(false)} style={{ color: '#e05b78' }}>
                <ChevronRightIcon />
              </IconButton>
            </div>
            
            <Typography variant="body2" color="textSecondary" gutterBottom>
              ПРОЗРАЧНОСТЬ: {(opacity * 100).toFixed(0)}%
            </Typography>
            <Slider value={opacity} min={0} max={1} step={0.01} onChange={handleOpacityChange} style={{ color: '#c15b75' }} />
            
            <Divider style={{ margin: '20px 0', backgroundColor: 'rgba(164, 49, 80, 0.3)' }} />
            
            <Typography variant="body2" color="textSecondary" gutterBottom>ИНСТРУМЕНТЫ</Typography>
            {canEdit ? (
              <Stack direction="row" spacing={1} style={{ marginBottom: 15 }}>
                <Button variant={drawType === 'Point' ? "contained" : "outlined"} onClick={() => setDrawType('Point')}>Точка</Button>
                <Button variant={drawType === 'LineString' ? "contained" : "outlined"} onClick={() => setDrawType('LineString')}>Линия</Button>
                <Button variant={drawType === 'Polygon' ? "contained" : "outlined"} onClick={() => setDrawType('Polygon')}>Полигон</Button>
              </Stack>
            ) : (
              <Alert severity="info" style={{ marginBottom: 15 }}>Только просмотр (Гость)</Alert>
            )}
            
            <Button variant="text" color="error" onClick={clearDrawings} fullWidth disabled={!canDelete}>Очистить всё</Button>
            
            {measureResult && (
              <div style={{ marginTop: 16, padding: 12, backgroundColor: 'rgba(164, 49, 80, 0.15)', borderRadius: 6, border: '1px solid rgba(164, 49, 80, 0.3)' }}>
                <Typography variant="body2" style={{ fontWeight: 'bold', color: '#c15b75' }}>{measureResult}</Typography>
              </div>
            )}
          </Paper>
        )}
      </div>

      <Drawer anchor="left" open={isDrawerOpen} onClose={() => setIsDrawerOpen(false)}>
        <div style={{ width: 350, padding: 16, height: '100%', backgroundColor: mode === 'dark' ? '#0a172b' : '#f4f6fa', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
          
          <div style={{ marginBottom: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)', borderRadius: 6, paddingRight: 8 }}>
              <ListItemButton onClick={() => toggleGroup('Ортофотопланы')} style={{ paddingTop: 8, paddingBottom: 8, flexGrow: 1 }}>
                <ListItemIcon style={{ minWidth: 30 }}><LayersIcon style={{ color: '#a43150' }} /></ListItemIcon>
                <ListItemText primary={<Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>ОРТОФОТОПЛАНЫ</Typography>} />
                {openGroups['Ортофотопланы'] ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
              </ListItemButton>
              
              <IconButton size="small" onClick={() => {
                  const allVisible = orthoList.every(o => o.visible !== false);
                  const newList = orthoList.map(o => ({ ...o, visible: !allVisible }));
                  setOrthoList(newList);
                  if (map) (map as any).orthoLayer.setOpacity(!allVisible ? opacity : 0);
              }}>
                {orthoList.every(o => o.visible !== false) ? <VisibilityOffIcon fontSize="small" style={{ color: '#e05b78' }} /> : <VisibilityIcon fontSize="small" style={{ color: '#22c55e' }} />}
              </IconButton>
            </div>
            
            <Collapse in={openGroups['Ортофотопланы']} timeout="auto" unmountOnExit>
              <div style={{ paddingTop: 8, paddingBottom: 8 }}>
                <TextField 
                  fullWidth size="small" variant="outlined" placeholder="Поиск по названию..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                  slotProps={{ input: { startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>) } }}
                  style={{ marginBottom: 10 }}
                />
                <List dense>
                  {filteredOrthos.map((ortho) => (
                    <ListItem key={ortho.id} style={{ backgroundColor: 'rgba(16, 37, 68, 0.6)', marginBottom: 5, borderRadius: 6, border: '1px solid #304463' }}>
                      <ListItemButton onClick={() => openOrthoInfo(ortho)} style={{ padding: 4 }}>
                        <ListItemText 
                          primary={<Typography style={{ color: mode === 'dark' ? '#f4f6fa' : '#0a172b', fontSize: '13px', fontWeight: 'bold' }}>{ortho.name}</Typography>} 
                          secondary={<span style={{ color: '#a9b4c7', fontSize: '11px' }}>Дата: {ortho.date}</span>} 
                        />
                      </ListItemButton>
                      
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); toggleOrthoVisibility(ortho.id); }}>
                         {ortho.visible !== false ? <VisibilityIcon fontSize="small" style={{ color: '#22c55e' }} /> : <VisibilityOffIcon fontSize="small" style={{ color: '#a9b4c7' }} />}
                      </IconButton>

                      <IconButton size="small" onClick={() => handleZoomToOrtho(ortho.x, ortho.y)} style={{ color: '#c15b75' }}>
                        <MapIcon fontSize="small" />
                      </IconButton>
                    </ListItem>
                  ))}
                </List>
              </div>
            </Collapse>
          </div>

          <Divider style={{ marginBottom: 8, backgroundColor: 'rgba(164, 49, 80, 0.3)' }} />

          <div style={{ marginBottom: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)', borderRadius: 6, paddingRight: 8 }}>
              <ListItemButton onClick={() => toggleGroup('Объекты')} style={{ paddingTop: 8, paddingBottom: 8, flexGrow: 1 }}>
                <ListItemIcon style={{ minWidth: 30 }}><LayersIcon style={{ color: '#a43150' }} /></ListItemIcon>
                <ListItemText primary={<Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>СПИСОК ОБЪЕКТОВ</Typography>} />
                {openGroups['Объекты'] ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
              </ListItemButton>
            </div>

            <Collapse in={openGroups['Объекты']} timeout="auto" unmountOnExit>
              <div style={{ paddingTop: 8 }}>
                <TextField 
                  fullWidth size="small" variant="outlined" placeholder="Поиск объекта..." value={objSearchQuery} onChange={(e) => setObjSearchQuery(e.target.value)}
                  slotProps={{ input: { startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>) } }}
                  style={{ marginBottom: 10 }}
                />

                {Object.keys(groupedObjects).map(group => renderGroupSection(group, groupedObjects[group], openObjectInfo, toggleFeatureVisibility))}
              </div>
            </Collapse>
          </div>
        </div>
      </Drawer>

      <Dialog open={isLoginModalOpen} onClose={() => setIsLoginModalOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle style={{ borderBottom: '1px solid rgba(164, 49, 80, 0.3)' }}>
          ВХОД В СИСТЕМУ ({targetRole === 'admin' ? 'Администратор' : 'Оператор'})
        </DialogTitle>
        <DialogContent style={{ paddingTop: 20 }}>
          {loginError && <Alert severity="error" style={{ marginBottom: 15 }}>{loginError}</Alert>}
          <TextField 
            autoFocus margin="dense" label="Логин" type="text" fullWidth variant="outlined" 
            value={loginUsername} onChange={(e) => setLoginUsername(e.target.value)} 
            slotProps={{ input: { startAdornment: (<InputAdornment position="start"><PersonIcon /></InputAdornment>) } }}
            style={{ marginBottom: 15 }} 
          />
          <TextField 
            margin="dense" label="Пароль" type="password" fullWidth variant="outlined" 
            value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} 
            slotProps={{ input: { startAdornment: (<InputAdornment position="start"><LockIcon /></InputAdornment>) } }}
          />
        </DialogContent>
        <DialogActions style={{ padding: 20 }}>
          <Button onClick={() => setIsLoginModalOpen(false)} color="error" variant="outlined" style={{ borderColor: '#e05b78', color: '#e05b78' }}>Отмена</Button>
          <Button onClick={handleLoginSubmit} color="primary" variant="contained">Войти</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={isInfoModalOpen} onClose={() => setIsInfoModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle style={{ borderBottom: '1px solid rgba(164, 49, 80, 0.3)', display: 'flex', justifyContent: 'space-between' }}>
          <span>ИНФОРМАЦИЯ ОБ ОБЪЕКТЕ</span>
          <IconButton onClick={() => setIsInfoModalOpen(false)} size="small" style={{ color: '#e05b78' }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers style={{ borderBottom: 'none' }}>
          {infoData && (
            <div>
              <Typography variant="h5" gutterBottom style={{ color: '#c15b75', fontWeight: 'bold' }}>{infoData.name}</Typography>
              <div style={{ marginTop: 16 }}>
                <Typography variant="body2" gutterBottom><strong style={{ color: '#a9b4c7' }}>ТИП:</strong> {getRussianType(infoData.type)}</Typography>
                <Typography variant="body2" gutterBottom><strong style={{ color: '#a9b4c7' }}>ОПИСАНИЕ:</strong> {infoData.description || 'Нет описания'}</Typography>
                {infoData.coords && (
                  <Typography variant="body2" gutterBottom><strong style={{ color: '#a9b4c7' }}>КООРДИНАТЫ:</strong> X={infoData.coords[0]}, Y={infoData.coords[1]}</Typography>
                )}
              </div>
              
              {renderMetrics()}

              {infoData.attributes && Object.keys(infoData.attributes).length > 0 && (
                <div style={{ marginTop: 24 }}>
                  <Typography variant="subtitle2" color="textSecondary"><strong>ХАРАКТЕРИСТИКИ:</strong></Typography>
                  <ul style={{ margin: '10px 0', paddingLeft: 20 }}>
                    {Object.entries(infoData.attributes).map(([key, val]: any) => (
                      <li key={key}><Typography variant="body2">{key}: {val}</Typography></li>
                    ))}
                  </ul>
                </div>
              )}
              {infoData.file_urls && infoData.file_urls.length > 0 && (
                <div style={{ marginTop: 24 }}>
                  <Typography variant="subtitle2" color="textSecondary"><strong>ВЛОЖЕНИЯ:</strong></Typography>
                  <ul style={{ margin: '10px 0', paddingLeft: 20 }}>
                    {infoData.file_urls.map((file: any, index: number) => (
                      <li key={index}><Link href={`http://10.5.10.101:8000${file.url}`} target="_blank" rel="noopener" style={{ color: '#c15b75' }}>📎 {file.name}</Link></li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </DialogContent>
        
        <div style={{ padding: 20, borderTop: '1px solid rgba(164, 49, 80, 0.3)' }}>
          <Stack direction="row" spacing={1} style={{ marginBottom: 15 }}>
             <Button onClick={() => zoomToFeature(infoData.id)} color="success" variant="outlined" startIcon={<GpsFixedIcon />} style={{ color: '#22c55e', borderColor: '#22c55e', flex: 1 }}>
               Показать
             </Button>
             {canEdit && (
               <Button onClick={openEditModal} color="primary" variant="outlined" startIcon={<EditIcon />} style={{ flex: 1 }}>
                 Редактировать
               </Button>
             )}
             {canDelete && (
               <Button onClick={() => confirmDeleteObject(infoData.id)} color="error" variant="outlined" startIcon={<DeleteIcon />} style={{ flex: 1 }}>
                 Удалить
               </Button>
             )}
          </Stack>
          <Button onClick={() => setIsInfoModalOpen(false)} color="primary" variant="contained" fullWidth>
            Закрыть
          </Button>
        </div>
      </Dialog>

      <Dialog open={isOrthoInfoModalOpen} onClose={() => setIsOrthoInfoModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle style={{ borderBottom: '1px solid rgba(164, 49, 80, 0.3)', display: 'flex', justifyContent: 'space-between' }}>
          <span>ИНФОРМАЦИЯ ОБ ОРТОФОТОПЛАНЕ</span>
          <IconButton onClick={() => setIsOrthoInfoModalOpen(false)} size="small" style={{ color: '#e05b78' }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers style={{ borderBottom: 'none' }}>
          {currentOrthoInfo && (
            <div>
              <Typography variant="h5" gutterBottom style={{ color: '#c15b75', fontWeight: 'bold' }}>{currentOrthoInfo.name}</Typography>
              <div style={{ marginTop: 16 }}>
                <Typography variant="body2" gutterBottom><strong style={{ color: '#a9b4c7' }}>ДАТА:</strong> {currentOrthoInfo.date}</Typography>
                <Typography variant="body2" gutterBottom><strong style={{ color: '#a9b4c7' }}>ОПИСАНИЕ:</strong> {currentOrthoInfo.desc || 'Нет описания'}</Typography>
              </div>
            </div>
          )}
        </DialogContent>
        
        <div style={{ padding: 20, borderTop: '1px solid rgba(164, 49, 80, 0.3)' }}>
           <Stack direction="row" spacing={1} style={{ marginBottom: 15 }}>
             <Button onClick={() => { handleZoomToOrtho(currentOrthoInfo.x, currentOrthoInfo.y); setIsOrthoInfoModalOpen(false); }} color="success" variant="outlined" startIcon={<GpsFixedIcon />} style={{ color: '#22c55e', borderColor: '#22c55e', flex: 1 }}>
               Показать
             </Button>
             {canEdit && (
               <Button color="primary" variant="outlined" startIcon={<EditIcon />} style={{ flex: 1 }} onClick={() => enqueueSnackbar("Редактирование ортофотопланов будет добавлено позже", { variant: 'info' })}>
                 Редактировать
               </Button>
             )}
           </Stack>
           <Button onClick={() => setIsOrthoInfoModalOpen(false)} color="primary" variant="contained" fullWidth>
             Закрыть
           </Button>
        </div>
      </Dialog>

      <Dialog open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
        <DialogTitle style={{ borderBottom: '1px solid rgba(164, 49, 80, 0.3)' }}>УДАЛЕНИЕ ОБЪЕКТА</DialogTitle>
        <DialogContent style={{ paddingTop: 20 }}>
          <Typography>Вы уверены, что хотите удалить этот объект? Это действие нельзя отменить.</Typography>
        </DialogContent>
        <DialogActions style={{ padding: 20 }}>
          <Button onClick={() => setDeleteModalOpen(false)} variant="outlined" style={{ borderColor: '#304463', color: mode === 'dark' ? '#f4f6fa' : '#0a172b' }}>Отмена</Button>
          <Button onClick={executeDelete} color="error" variant="contained">Удалить</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={isCreateModalOpen} onClose={handleCancelObject} maxWidth="sm" fullWidth>
        <DialogTitle style={{ borderBottom: '1px solid rgba(164, 49, 80, 0.3)', display: 'flex', justifyContent: 'space-between' }}>
          <span>{editingObjectId ? 'РЕДАКТИРОВАНИЕ ОБЪЕКТА' : 'СОЗДАНИЕ ОБЪЕКТА'}</span>
          <IconButton onClick={handleCancelObject} size="small" style={{ color: '#e05b78' }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers style={{ borderBottom: 'none' }}>
          <TextField autoFocus margin="dense" label="Название объекта" type="text" fullWidth variant="outlined" value={objectName} onChange={(e) => setObjectName(e.target.value)} style={{ marginBottom: 20 }} />
          <FormControl fullWidth variant="outlined" style={{ marginBottom: 20 }}>
            <InputLabel>Категория</InputLabel>
            <Select value={objectCategory} onChange={(e) => setObjectCategory(e.target.value as string)} label="Категория">
              <MenuItem value="Инфраструктура">Инфраструктура</MenuItem>
              <MenuItem value="Гидрография">Гидрография</MenuItem>
              <MenuItem value="Растительность">Растительность</MenuItem>
              <MenuItem value="Другое">Другое</MenuItem>
            </Select>
          </FormControl>
          <TextField margin="dense" label="Описание" multiline rows={3} fullWidth variant="outlined" value={objectDesc} onChange={(e) => setObjectDesc(e.target.value)} style={{ marginBottom: 20 }} />
          
          <TextField 
            margin="dense" 
            label="Высота (м)" 
            type="number" 
            fullWidth 
            variant="outlined" 
            value={objectHeight} 
            onChange={(e) => setObjectHeight(Number(e.target.value))} 
            style={{ marginBottom: 20 }} 
          />

          <FormControl fullWidth variant="outlined" style={{ marginBottom: 20 }}>
            <InputLabel>Пиктограмма</InputLabel>
            <Select value={objectIcon} onChange={(e) => setObjectIcon(e.target.value as string)} label="Пиктограмма">
              <MenuItem value="default">По умолчанию (красная точка)</MenuItem>
              <MenuItem value="blue">Синий маркер</MenuItem>
              <MenuItem value="green">Зеленый маркер</MenuItem>
              <MenuItem value="orange">Оранжевый маркер</MenuItem>
              <MenuItem value="violet">Фиолетовый маркер</MenuItem>
            </Select>
          </FormControl>
          <Typography variant="subtitle2" color="textSecondary" gutterBottom>ДОПОЛНИТЕЛЬНЫЕ ХАРАКТЕРИСТИКИ</Typography>
          {customFields.map((field, index) => (
            <div key={index} style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
              <TextField label="Свойство" value={field.key} onChange={(e) => handleFieldChange(index, 'key', e.target.value)} size="small" style={{ marginRight: 10 }} />
              <TextField label="Значение" value={field.value} onChange={(e) => handleFieldChange(index, 'value', e.target.value)} size="small" style={{ marginRight: 10 }} />
              <IconButton onClick={() => handleRemoveField(index)} style={{ color: '#e05b78' }}><DeleteIcon /></IconButton>
            </div>
          ))}
          <Button startIcon={<AddIcon />} onClick={handleAddField} size="small" style={{ marginBottom: 20, color: '#c15b75' }}>Добавить поле</Button>
          <Typography variant="subtitle2" color="textSecondary" gutterBottom>ВЛОЖЕНИЯ</Typography>
          <Button variant="outlined" component="label" fullWidth style={{ marginBottom: 10, borderColor: '#304463' }}>
            📎 Прикрепить файл
            <input type="file" hidden onChange={handleFileUpload} />
          </Button>
          <List dense>
            {uploadedFiles.map((file, idx) => (
              <ListItem key={idx} style={{ backgroundColor: 'rgba(48, 68, 99, 0.4)', marginBottom: 5, borderRadius: 6 }}>
                <ListItemText primary={file.name} secondary={file.url} />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions style={{ padding: 20 }}>
          <Button onClick={handleCancelObject} color="error" variant="outlined" style={{ borderColor: '#e05b78', color: '#e05b78' }}>Отмена</Button>
          <Button onClick={handleSaveObject} color="primary" variant="contained">{editingObjectId ? 'Сохранить изменения' : 'Создать'}</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

function App() {
  const [mode, setMode] = useState<'light' | 'dark'>(() => {
    const savedMode = localStorage.getItem('themeMode');
    return (savedMode as 'light' | 'dark') || 'dark';
  });

  const toggleTheme = () => {
    const newMode = mode === 'dark' ? 'light' : 'dark';
    setMode(newMode);
    localStorage.setItem('themeMode', newMode);
  };

  const theme = mode === 'dark' ? darkTheme : lightTheme;

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <SnackbarProvider maxSnack={3} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <MapApp toggleTheme={toggleTheme} mode={mode} />
      </SnackbarProvider>
    </ThemeProvider>
  );
}

export default App;
